import { useEffect, useRef, useState, type ReactNode } from 'react'

export const APP_FRAME_ID = 'anchor-app-frame'

const APP_FRAME_WIDTH = 375
const APP_FRAME_HEIGHT = 812
const APP_DESKTOP_GUTTER = 32
const APP_DESKTOP_MAX_WIDTH = 430
const APP_MOBILE_FULLSCREEN_MAX_WIDTH = 520

export function getAppFrameElement() {
  return document.getElementById(APP_FRAME_ID)
}

export function AppViewport({ children }: { children: ReactNode }) {
  const viewportRef = useRef<HTMLDivElement | null>(null)
  const [isMobileFullscreen, setIsMobileFullscreen] = useState(false)

  useEffect(() => {
    const element = viewportRef.current
    if (!element || typeof window === 'undefined') {
      return
    }

    const updateVisualViewport = () => {
      const visualViewport = window.visualViewport
      const visualWidth = visualViewport?.width ?? window.innerWidth
      const visualHeight = visualViewport?.height ?? window.innerHeight
      const offsetLeft = visualViewport?.offsetLeft ?? 0
      const offsetTop = visualViewport?.offsetTop ?? 0
      const layoutWidth = window.innerWidth
      const layoutHeight = window.innerHeight
      const availableWidth = Math.max(1, layoutWidth - APP_DESKTOP_GUTTER)
      const availableHeight = Math.max(1, layoutHeight - APP_DESKTOP_GUTTER)
      const responsiveDesktopWidth = Math.min(
        APP_DESKTOP_MAX_WIDTH,
        Math.max(320, visualWidth - APP_DESKTOP_GUTTER),
        availableWidth,
        availableHeight * APP_FRAME_WIDTH / APP_FRAME_HEIGHT
      )
      const screenWidth = Math.min(window.screen?.width || layoutWidth, window.screen?.height || layoutHeight)
      const narrowVisualViewport = Math.min(visualWidth, layoutWidth) <= APP_MOBILE_FULLSCREEN_MAX_WIDTH
      const mobileFullscreen = narrowVisualViewport || screenWidth <= APP_MOBILE_FULLSCREEN_MAX_WIDTH
      const viewportLeft = mobileFullscreen ? offsetLeft : 0
      const viewportTop = mobileFullscreen ? offsetTop : 0
      const viewportWidth = mobileFullscreen ? visualWidth : layoutWidth
      const viewportHeight = mobileFullscreen ? visualHeight : layoutHeight
      const shellWidth = mobileFullscreen ? visualWidth : responsiveDesktopWidth

      element.style.setProperty('--app-visual-left', `${viewportLeft}px`)
      element.style.setProperty('--app-visual-top', `${viewportTop}px`)
      element.style.setProperty('--app-visual-width', `${viewportWidth}px`)
      element.style.setProperty('--app-visual-height', `${viewportHeight}px`)
      element.style.setProperty('--app-shell-width', `${shellWidth}px`)
      setIsMobileFullscreen(mobileFullscreen)
    }

    updateVisualViewport()
    window.addEventListener('resize', updateVisualViewport)
    window.visualViewport?.addEventListener('resize', updateVisualViewport)
    window.visualViewport?.addEventListener('scroll', updateVisualViewport)

    return () => {
      window.removeEventListener('resize', updateVisualViewport)
      window.visualViewport?.removeEventListener('resize', updateVisualViewport)
      window.visualViewport?.removeEventListener('scroll', updateVisualViewport)
    }
  }, [])

  return (
    <div ref={viewportRef} className={`app-viewport${isMobileFullscreen ? ' is-mobile-fullscreen' : ''}`}>
      <div className="app-scale-shell">
        <div className="app-frame" id={APP_FRAME_ID}>
          {children}
        </div>
      </div>
    </div>
  )
}
